// Backend health check utility
export interface HealthCheckResult {
  isHealthy: boolean;
  responseTime?: number;
  error?: string;
  endpoint: string;
}

export async function checkBackendHealth(): Promise<HealthCheckResult> {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
  const healthEndpoint = `${apiUrl}/health`; // Assuming there's a health endpoint
  
  const startTime = Date.now();
  
  try {
    const response = await fetch(healthEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Short timeout for health check
      signal: AbortSignal.timeout(5000)
    });
    
    const responseTime = Date.now() - startTime;
    
    if (response.ok) {
      return {
        isHealthy: true,
        responseTime,
        endpoint: healthEndpoint
      };
    } else {
      return {
        isHealthy: false,
        responseTime,
        error: `HTTP ${response.status}: ${response.statusText}`,
        endpoint: healthEndpoint
      };
    }
  } catch (error: any) {
    return {
      isHealthy: false,
      responseTime: Date.now() - startTime,
      error: error.message || 'Connection failed',
      endpoint: healthEndpoint
    };
  }
}

// Check specific endpoint health
export async function checkEndpointHealth(endpoint: string, requiresAuth: boolean = false): Promise<HealthCheckResult> {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
  const fullEndpoint = `${apiUrl}${endpoint}`;
  
  const startTime = Date.now();
  
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (requiresAuth) {
      const token = localStorage.getItem('token'); // Fixed: using 'token' not 'jwtToken'
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await fetch(fullEndpoint, {
      method: 'HEAD', // Use HEAD to avoid getting response body
      headers,
      signal: AbortSignal.timeout(5000)
    });
    
    const responseTime = Date.now() - startTime;
    
    // For authenticated endpoints, 401 means the endpoint exists but needs auth
    if (response.ok || (requiresAuth && response.status === 401)) {
      return {
        isHealthy: true,
        responseTime,
        endpoint: fullEndpoint
      };
    } else {
      return {
        isHealthy: false,
        responseTime,
        error: `HTTP ${response.status}: ${response.statusText}`,
        endpoint: fullEndpoint
      };
    }
  } catch (error: any) {
    return {
      isHealthy: false,
      responseTime: Date.now() - startTime,
      error: error.message || 'Connection failed',
      endpoint: fullEndpoint
    };
  }
}

// Get all failed requests from localStorage
export function getFailedWantListRequests(): any[] {
  try {
    return JSON.parse(localStorage.getItem('failedWantListRequests') || '[]');
  } catch {
    return [];
  }
}

// Check current user ID from JWT token
export function getCurrentUserId(): number | null {
  try {
    const token = localStorage.getItem('token'); // Fixed: using 'token' not 'jwtToken'
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId || payload.id || null;
    }
    return null;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
}

// Clear failed requests from localStorage
export function clearFailedWantListRequests(): void {
  localStorage.removeItem('failedWantListRequests');
}

// Retry a specific failed request
export async function retryFailedRequest(request: any, apiCall: (payload: any) => Promise<any>): Promise<any> {
  try {
    const result = await apiCall(request.payload);
    
    // Remove this request from failed requests on success
    const failedRequests = getFailedWantListRequests();
    const updatedRequests = failedRequests.filter(r => 
      r.timestamp !== request.timestamp || 
      JSON.stringify(r.payload) !== JSON.stringify(request.payload)
    );
    localStorage.setItem('failedWantListRequests', JSON.stringify(updatedRequests));
    
    return result;
  } catch (error) {
    throw error;
  }
}